<div class="page-container">
  <app-card>
    <app-card-header>
      <app-card-title>
        <div class="header-row">
          <div class="form-actions">
            @if (isEditMode() && canDelete()) {
            <app-button class="" variant="outlined" color="danger" type="button" (click)="openDeleteModal()"
              [disabled]="saving() || deleting()" data-testid="delete-form-button">
              <app-icon name="trash-2" />
              <span>{{ 'form.delete.button' | translate }}</span>
            </app-button>
            }
            <app-button variant="outlined" type="button" (click)="onCancel()" [disabled]="saving()"
              data-testid="cancel-form-button">
              <app-icon name="x" />
              {{ 'button.cancel' | translate }}
            </app-button>
            @if (canEdit()) {
            <app-button class="form-action-button form-action-button-feedback" variant="outlined" type="button"
              (click)="onSave()" [disabled]="saving()" data-testid="save-form-button">
              <span class="button-face" [class.is-hidden]="saveButtonFeedback() !== 'idle'">
                <app-icon name="save" />
                <span>{{ 'form.form.save' | translate }}</span>
              </span>
              <span class="button-feedback" [class.is-visible]="saveButtonFeedback() === 'saving'"
                data-testid="save-form-button-saving">
                <app-spinner [diameter]="20"></app-spinner>
                <span>{{ 'form.form.saving' | translate }}</span>
              </span>
              <span class="button-feedback button-feedback-success"
                [class.is-visible]="saveButtonFeedback() === 'saved'" data-testid="save-form-button-saved">
                <app-icon name="check" />
                <span>{{ 'form.form.saved' | translate }}</span>
              </span>
            </app-button>
            <app-button class="form-action-button form-action-button-wide" variant="filled" type="button"
              (click)="onSaveAndClose()" [disabled]="saving()" data-testid="save-and-close-form-button">
              @if (saving() && activeSaveAction() === 'saveAndClose') {
              <app-spinner [diameter]="20"></app-spinner>
              <span>{{ 'form.form.saving' | translate }}</span>
              } @else {
              <app-icon name="save" />
              <span>{{ 'form.form.saveAndClose' | translate }}</span>
              }
            </app-button>
            }
          </div>
        </div>
      </app-card-title>
    </app-card-header>

    <app-card-content>
      @if (loading()) {
      <div class="loading-container">
        <app-spinner></app-spinner>
        <p>{{ 'form.form.loading' | translate }}</p>
      </div>
      } @else if (error()) {
      <div class="error-message">
        <p>{{ error() }}</p>
      </div>
      } @else {
      <form [formGroup]="form" (ngSubmit)="onSubmit()">
        <app-tab-strip [(selected)]="activeTab">
          <!-- Details Tab -->
          <app-tab id="details" label="{{ 'form.form.tabs.details' | translate }}">
            <formly-form [form]="form" [fields]="fields" [model]="model">
            </formly-form>

            <!-- Custom Form Fields -->
            @if (canEdit()) {
            <div class="form-builder-section">
              <app-form-builder [initialFields]="customFormFields()" [initialColumns]="formColumns()"
                (fieldsChanged)="onCustomFormFieldsChanged($event)" (columnsChanged)="onColumnsChanged($event)">
              </app-form-builder>
            </div>
            } @else if (customFormFields().length > 0) {
            <!-- Read-only view of custom fields -->
            <div class="form-builder-section">
              <h3>{{ 'form.form.customFields' | translate }}</h3>
              <div class="readonly-fields">
                @for (field of customFormFields(); track field.id) {
                <div class="readonly-field-card">
                  <div class="field-type-icon">
                    <app-icon [name]="getFieldIcon(getFieldType(field))" />
                  </div>
                  <div class="field-details">
                    <strong>{{ field.props?.label || field.key }}</strong>
                    <small>{{ field.key }} ({{ getFieldType(field) }})</small>
                  </div>
                </div>
                }
              </div>
            </div>
            }
          </app-tab>

          <!-- Assets/Checkpoints Tab -->
          @if (isEditMode()) {
          <app-tab id="assets" label="{{ 'form.form.tabs.assets' | translate }}">
            @if (checkPointsLoading()) {
            <div class="loading-container">
              <app-spinner></app-spinner>
              <p>{{ 'form.form.loadingAssets' | translate }}</p>
            </div>
            } @else if (checkPointsError()) {
            <div class="error-message">
              <p>{{ checkPointsError() }}</p>
            </div>
            } @else if (checkPoints().length === 0) {
            <div class="empty-state">
              <app-icon name="package" />
              <p>{{ 'form.form.noAssets' | translate }}</p>
              <small>{{ 'form.form.noAssetsHint' | translate }}</small>
            </div>
            } @else {
            <div class="check-point-grid">
              @for (checkPoint of checkPoints(); track checkPoint.id) {
              <div class="checkPoint-card" [attr.data-testid]="'asset-card-' + checkPoint.id">
                <div class="checkPoint-card-icon">
                  <app-icon name="package" />
                </div>
                <div class="checkPoint-card-content">
                  <h4><a [routerLink]="['/checkpoints', checkPoint.id]">{{ checkPoint.name }}</a></h4>
                  @if (checkPoint.description) {
                  <p>{{ checkPoint.description }}</p>
                  }
                  @if (getInstancesFor(checkPoint).length > 0) {
                  <div class="checkPoint-instance-list" data-testid="checkpoint-instance-list">
                    @for (instance of getInstancesFor(checkPoint); track instance.id) {
                    <div class="checkPoint-instance-row" [attr.data-testid]="'instance-row-' + instance.id">
                      <span class="checkPoint-instance-label">
                        {{ 'chooseInstance.unitLabel' | translate: { serial: instance.serial_number } }}
                      </span>
                      <app-button variant="icon" type="button"
                        (click)="recordInteractionForInstance(checkPoint, instance)"
                        [tippy]="'form.form.recordInteraction' | translate">
                        <app-icon name="file-pen-line" />
                      </app-button>
                    </div>
                    }
                  </div>
                  }
                </div>
                <div class="checkPoint-card-actions">
                  <app-button variant="icon" type="button" (click)="showCheckPointQrCode(checkPoint)"
                    [tippy]="'form.form.showQrCode' | translate">
                    <app-icon name="qr-code" />
                  </app-button>
                  <app-button variant="icon" type="button" (click)="showFormView(checkPoint)"
                    [tippy]="'form.form.viewForm' | translate">
                    <app-icon name="eye" />
                  </app-button>
                  @if (getInstancesFor(checkPoint).length === 0) {
                  <app-button variant="icon" type="button" (click)="recordInteraction(checkPoint)"
                    [tippy]="'form.form.recordInteraction' | translate">
                    <app-icon name="file-pen-line" />
                  </app-button>
                  }
                  <app-button variant="icon" type="button" (click)="viewFormSubmission(checkPoint)"
                    [tippy]="'form.form.viewHistory' | translate">
                    <app-icon name="history" />
                  </app-button>
                </div>
              </div>
              }
            </div>
            }
          </app-tab>
          }
        </app-tab-strip>
      </form>
      }
    </app-card-content>
  </app-card>

  @if (showDeleteModal()) {
  <app-modal-dialog (backdropClick)="cancelDelete()">
    <div header>
      <div class="modal-title-row">
        <div class="heading-md">{{ 'form.delete.title' | translate }}</div>
        <app-button variant="icon" (click)="cancelDelete()">
          <app-icon name="x" />
        </app-button>
      </div>
    </div>
    <div content>
      <p>{{ 'form.delete.confirm' | translate:{ name: model.name } }}</p>
      <p>{{ 'form.delete.warning' | translate }}</p>
    </div>
    <div footer>
      <app-button variant="text" (click)="cancelDelete()">
        {{ 'button.cancel' | translate }}
      </app-button>
      <app-button color="danger" (click)="confirmDelete()">
        {{ 'form.delete.button' | translate }}
      </app-button>
    </div>
  </app-modal-dialog>
  }
</div>